// @ts-nocheck
import { ApiHandler } from "@insite/client-framework/HandlerCreator";
import {
    createSession,
    Session,
    stopWatchingForOtherTabSessionChange,
    watchForOtherTabSessionChange,
} from "@insite/client-framework/Services/SessionService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { SignInParameter, SignInResult } from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    chain,
    RequestAccessToken as baseRequestAccessToken,
    RequestSession as baseRequestSession,
} from "@insite/client-framework/Store/Context/Handlers/SignIn";
import ga from "@store/Framework/Common/GoogleAnalytics";
import { replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

type HandlerType = ApiHandler<
    SignInParameter,
    SignInResult,
    {
        tokenSuccessfullyRequested?: boolean;
        authenticatedSession?: Session;
    }
>;

export const RequestAccessToken: HandlerType = async props => {
    const { enableTokenAuthentication } = getSettingsCollection(props.getState()).websiteSettings;
    const currentUserIsGuest = getCurrentUserIsGuest(props.getState());
    if (!enableTokenAuthentication && !currentUserIsGuest) {
        props.tokenSuccessfullyRequested = true;
        return;
    }

    const data = new URLSearchParams();
    data.append("grant_type", "password");
    data.append("username", props.parameter.userName);
    data.append("password", props.parameter.password);
    data.append("scope", "iscapi offline_access");

    // Store Custom Code
    // eslint-disable-next-line spire/no-node-fetch
    const response = await fetch("/identity/connect/token", {
        method: "POST",
        body: data.toString(),
        headers: new Headers({
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${btoa("isc:009AC476-B28E-4E33-8BAE-B5F103A142BC")}`,
        }),
    });

    props.apiResult = await (response.json() as Promise<{
        readonly access_token: string;
        readonly refresh_token: string;
        readonly expires_in: number;
        readonly error_description: string;
    }>);

    props.tokenSuccessfullyRequested = response.ok;

    if (!response.ok) {
        props.parameter.onError?.(props.apiResult.error_description);

        /* Store Custom Code - Start */
        ga.push({
            event: "website_error",
            error_type: props.apiResult.error_description,
        });
        /* Store Custom Code - End */
    }
};

export const RequestSession: HandlerType = async props => {
    if (!props.tokenSuccessfullyRequested) {
        return;
    }

    const { password, rememberMe, keepMeSignedIn, returnUrl, userName } = props.parameter;

    stopWatchingForOtherTabSessionChange(); // Prevent detection of the change we're making here.
    const session = await createSession({
        password,
        rememberMe,
        keepMeSignedIn,
        returnUrl,
        userName,
        accessToken: props.apiResult?.access_token,
    });

    if (!session.successful) {
        watchForOtherTabSessionChange(); // This sign-in failed, but another tab might be successful later on.
        props.parameter.onError?.(session.errorMessage, session.statusCode);
        return;
    }

    props.dispatch({
        type: "Context/CompleteLoadSession",
        session: session.result,
    });

    props.authenticatedSession = session.result;

    /* Store Custom Code - Start */
    ga.push({
        event: "login",
        authentication_method: "userName",
        authentication_userName: props.authenticatedSession.userName,
    });
    /* Store Custom Code - End */
};

replaceInChain(chain, baseRequestAccessToken, RequestAccessToken);
replaceInChain(chain, baseRequestSession, RequestSession);
